// GPX Import Flow — right-side panel. Pick a .gpx file → review the parsed
// content (areas, trails, waypoint→pin mapping with remap for unknowns) →
// confirm, which injects the geometry into the editor as fresh, editable objects.
// CLUB-188 (Import Map) / CLUB-186 (pin-type mapping).

import { useState, useRef, type ReactNode } from 'react';
import { useTranslation } from '../i18n';
import { COLORS } from '../lib/theme';
import { pinSrc, type PinKind } from './MapPin';
import { usePinTypeCatalog } from './PinTypeCatalogProvider';
import { Icon } from './Icon';
import type { CopyableTeamMap } from '../lib/types';
import {
  parseGpx, buildImportModel, isEmptyModel, MAX_GPX_BYTES,
  type GpxImportModel, type GpxImportPin, type GpxImportArea,
} from '../lib/gpx';

// The area layers the review step lists, in display order. 'boundary' is the
// opt-in use of the longest untyped path as the ground's border; the other three
// are WeHunt's own tagged layers.
type AreaLayerKey = 'borders' | 'subAreas' | 'forbidden' | 'boundary';

// ── Types ─────────────────────────────────────────────────────────────────

export type GpxPinCategory = {
  type: PinKind;
  label: string;
  color: string;
  group: string;
};

type GpxStage = 'pick' | 'error_invalid' | 'error_empty' | 'error_toolarge' | 'review' | 'done';

export interface GpxImportFlowProps {
  pinCategories: GpxPinCategory[];
  hasExistingBorder: boolean;
  onClose: () => void;
  /** Inject the reviewed geometry into the editor (reuses the copy-map append). */
  onApply: (src: CopyableTeamMap) => void;
}

// ── Component ─────────────────────────────────────────────────────────────

export function GpxImportFlow({ pinCategories, hasExistingBorder, onClose, onApply }: GpxImportFlowProps) {
  const { t } = useTranslation('map');
  const catalog = usePinTypeCatalog();
  const [stage, setStage] = useState<GpxStage>('pick');
  const [model, setModel] = useState<GpxImportModel | null>(null);
  const [filename, setFilename] = useState('');
  const [includedLayers, setIncludedLayers] = useState<Set<AreaLayerKey>>(new Set());
  const [includeTrails, setIncludeTrails] = useState(true);
  const [assign, setAssign] = useState<Record<string, PinKind>>({});
  const [includedCats, setIncludedCats] = useState<Set<PinKind>>(new Set());
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [summary, setSummary] = useState<ImportSummary>({ areas: 0, trails: 0, pins: 0 });

  const onFile = async (file: File) => {
    if (file.size > MAX_GPX_BYTES) { setStage('error_toolarge'); return; }
    let parsed: GpxImportModel;
    try {
      const text = await file.text();
      parsed = buildImportModel(parseGpx(text), catalog.isKnown);
    } catch {
      setStage('error_invalid');
      return;
    }
    if (isEmptyModel(parsed)) { setStage('error_empty'); return; }
    setModel(parsed);
    setFilename(file.name);
    setAssign(Object.fromEntries(parsed.pins.map((p) => [p.id, p.suggest])));
    setIncludedCats(new Set(parsed.pins.map((p) => p.suggest)));
    const layers = new Set<AreaLayerKey>();
    if (parsed.borders.length) layers.add('borders');
    if (parsed.subAreas.length) layers.add('subAreas');
    if (parsed.forbiddenAreas.length) layers.add('forbidden');
    // 'boundary' starts unticked: an untyped path is a trail unless the user
    // says it is the ground's border.
    setIncludedLayers(layers);
    setIncludeTrails(true);
    setStage('review');
  };

  const categorySummary = (() => {
    if (!model) return [] as { cat: GpxPinCategory; items: GpxImportPin[] }[];
    const m = new Map<PinKind, GpxImportPin[]>();
    for (const p of model.pins) {
      const type = assign[p.id] ?? p.suggest;
      if (!m.has(type)) m.set(type, []);
      m.get(type)!.push(p);
    }
    return pinCategories.filter((c) => m.has(c.type)).map((c) => ({ cat: c, items: m.get(c.type)! }));
  })();

  const unknownPins = model ? model.pins.filter((p) => assign[p.id] === 'other') : [];
  const includedPinCount = model ? model.pins.filter((p) => includedCats.has(assign[p.id])).length : 0;
  const allCatsChecked = categorySummary.length > 0 && includedCats.size === categorySummary.length;

  // The area layers actually present in this file, in display order, each with
  // its icon, label and count. A third-party file has only the untyped boundary.
  const areaLayers: { key: AreaLayerKey; icon: string; label: string; count: number }[] = model
    ? ([
        model.borders.length && { key: 'borders' as const, icon: 'shield', label: t('gpx.section.borders'), count: model.borders.length },
        model.subAreas.length && { key: 'subAreas' as const, icon: 'subarea', label: t('gpx.section.subAreas'), count: model.subAreas.length },
        model.forbiddenAreas.length && { key: 'forbidden' as const, icon: 'forbidden-area', label: t('gpx.section.forbidden'), count: model.forbiddenAreas.length },
        model.boundary && { key: 'boundary' as const, icon: 'shield', label: t('gpx.boundaryFromPath'), count: 1 },
      ].filter(Boolean) as { key: AreaLayerKey; icon: string; label: string; count: number }[])
    : [];

  // Count of area polygons that would be imported with the current selection.
  // Derived from areaLayers so the footer total can never disagree with the
  // per-layer badges it is summing.
  const includedAreaCount = areaLayers.reduce(
    (n, l) => n + (includedLayers.has(l.key) ? l.count : 0), 0);

  // The trails an import would bring in under `layers`. The path taken as the
  // boundary is left out, so one path never lands on the map twice.
  const trailsFor = (layers: Set<AreaLayerKey>) => {
    if (!model || !includeTrails) return [];
    const usedAsBoundary = layers.has('boundary') ? model.boundary?.trailId : undefined;
    return model.trails.filter((tr) => tr.id !== usedAsBoundary);
  };
  const availableTrailCount = model
    ? model.trails.filter((tr) => !(includedLayers.has('boundary') && tr.id === model.boundary?.trailId)).length
    : 0;
  const includedTrailCount = includeTrails ? availableTrailCount : 0;

  const toggleLayer = (key: AreaLayerKey) => {
    const next = new Set(includedLayers);
    if (next.has(key)) next.delete(key); else next.add(key);
    setIncludedLayers(next);
  };

  const doImport = (layers: Set<AreaLayerKey> = includedLayers) => {
    if (!model) return;
    const inc = (k: AreaLayerKey) => layers.has(k);
    const toArea = (a: GpxImportArea) => ({ id: a.id, name: a.name || t('gpx.area.unnamed'), rings: [a.points] });
    const pins = model.pins.filter((p) => includedCats.has(assign[p.id]));
    const boundaryAreas = [
      ...(inc('borders') ? model.borders.map(toArea) : []),
      ...(inc('boundary') && model.boundary ? [{ id: 'gpx-boundary', name: t('gpx.boundaryName'), rings: [model.boundary.points] }] : []),
    ];
    const trails = trailsFor(layers);
    const src: CopyableTeamMap = {
      boundaryAreas,
      subAreaPolygons: inc('subAreas') ? model.subAreas.map(toArea) : [],
      forbiddenPolygons: inc('forbidden') ? model.forbiddenAreas.map(toArea) : [],
      objects: pins.map((p) => ({ id: p.id, type: assign[p.id], lat: p.lat, lon: p.lon, label: p.name })),
      // Untyped tracks and routes arrive as open trails, the way the app imports
      // them. Style is left to the line defaults, as for a fresh line on mobile.
      lines: trails.map((tr) => ({
        id: tr.id,
        kind: 'trail' as const,
        points: tr.points,
        ...(tr.name ? { name: tr.name } : {}),
      })),
    };
    const areas = boundaryAreas.length + src.subAreaPolygons.length + src.forbiddenPolygons.length;
    onApply(src);
    setSummary({ areas, trails: trails.length, pins: pins.length });
    setStage('done');
  };

  // The replace prompt guards the existing ground boundary only. "Add anyway"
  // imports everything as selected; "without boundary" drops the border/boundary
  // layers but still brings in sub-areas, forbidden areas and pins.
  const importsBoundary = includedLayers.has('borders') || includedLayers.has('boundary');
  const handleReviewContinue = () => {
    if (importsBoundary && hasExistingBorder) setShowReplaceDialog(true);
    else doImport();
  };
  const importWithoutBoundary = () => {
    const next = new Set(includedLayers);
    next.delete('borders');
    next.delete('boundary');
    setIncludedLayers(next);
    setShowReplaceDialog(false);
    doImport(next);
  };

  return (
    <Panel>
      {stage === 'pick' && <PickStage onClose={onClose} onFile={onFile} />}

      {(stage === 'error_invalid' || stage === 'error_empty' || stage === 'error_toolarge') && (
        <ErrorStage
          kind={stage === 'error_invalid' ? 'invalid' : stage === 'error_empty' ? 'empty' : 'toolarge'}
          onClose={onClose}
          onRetry={() => setStage('pick')}
        />
      )}

      {stage === 'review' && model && (
        <ReviewStage
          filename={filename}
          areaLayers={areaLayers}
          includedLayers={includedLayers}
          toggleLayer={toggleLayer}
          trailCount={availableTrailCount}
          includeTrails={includeTrails}
          setIncludeTrails={setIncludeTrails}
          includedTrailCount={includedTrailCount}
          categorySummary={categorySummary}
          includedCats={includedCats}
          setIncludedCats={setIncludedCats}
          allCatsChecked={allCatsChecked}
          unknownPins={unknownPins}
          assign={assign}
          setAssign={setAssign}
          pinCategories={pinCategories}
          includedPinCount={includedPinCount}
          includedAreaCount={includedAreaCount}
          onClose={onClose}
          onContinue={handleReviewContinue}
        />
      )}

      {stage === 'done' && <DoneStage summary={summary} onClose={onClose} />}

      {showReplaceDialog && (
        <ReplaceBoundaryDialog
          onAddAnyway={() => { setShowReplaceDialog(false); doImport(); }}
          onWithoutBoundary={importWithoutBoundary}
          onCancel={() => setShowReplaceDialog(false)}
        />
      )}
    </Panel>
  );
}

// ── Panel shell ───────────────────────────────────────────────────────────

function Panel({ children }: { children: ReactNode }) {
  return (
    <div
      data-map-panel="true"
      data-map-print="hide"
      style={{
        position: 'absolute', top: 16, right: 60, width: 320,
        background: COLORS.surfaceContainerLowest, borderRadius: 12,
        boxShadow: '0 8px 28px rgba(0,0,0,0.35)', overflow: 'hidden', zIndex: 3,
        maxHeight: 'calc(100% - 32px)', display: 'flex', flexDirection: 'column',
      }}
    >
      {children}
    </div>
  );
}

function PanelHeader({ title, subtitle, onClose }: { title: string; subtitle?: string; onClose: () => void }) {
  const { t } = useTranslation('map');
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', height: 52,
        flexShrink: 0, borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.onSurface, lineHeight: 1.2 }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 11, color: COLORS.outline, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {subtitle}
          </div>
        )}
      </div>
      <button className="wh-btn-icon" onClick={onClose} aria-label={t('gpx.close')}>
        <Icon name="x" size={18} />
      </button>
    </div>
  );
}

function StepBar({ step }: { step: 1 | 2 }) {
  const { t } = useTranslation('map');
  const steps = [
    { n: 1 as const, label: t('gpx.step.review') },
    { n: 2 as const, label: t('gpx.step.import') },
  ];
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', padding: '8px 16px',
        borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
        background: COLORS.surfaceContainerLow, flexShrink: 0,
      }}
    >
      {steps.map((s, i) => {
        const done = step > s.n;
        const active = step === s.n;
        return (
          <div key={s.n} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div
                style={{
                  width: 22, height: 22, borderRadius: 999, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  background: done || active ? COLORS.primary : COLORS.surfaceContainerHighest,
                  color: done || active ? COLORS.onPrimary : COLORS.outline,
                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}
              >
                {done ? <Icon name="check" size={13} /> : s.n}
              </div>
              <span style={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? COLORS.onSurface : COLORS.outline, whiteSpace: 'nowrap' }}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: COLORS.outlineVariant, margin: '0 8px' }} />}
          </div>
        );
      })}
    </div>
  );
}

function PanelFooter({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
        borderTop: `1px solid ${COLORS.surfaceContainerHighest}`, flexShrink: 0,
        background: COLORS.surfaceContainerLowest,
      }}
    >
      {children}
    </div>
  );
}

// ── 1) Pick stage ─────────────────────────────────────────────────────────

function PickStage({ onClose, onFile }: { onClose: () => void; onFile: (f: File) => void }) {
  const { t } = useTranslation('map');
  const [over, setOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <PanelHeader title={t('gpx.title')} onClose={onClose} />
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        <div
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${over ? COLORS.primary : COLORS.outlineVariant}`,
            borderRadius: 12, background: over ? COLORS.primaryOpacity8 : COLORS.surfaceContainerLow,
            padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
            transition: 'border-color 0.15s, background 0.15s',
          }}
        >
          <div
            style={{
              width: 52, height: 52, borderRadius: 999, background: COLORS.primaryContainer,
              color: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 12px',
            }}
          >
            <Icon name="upload" size={24} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface, marginBottom: 4 }}>
            {t('gpx.pick.drop')}
          </div>
          <div style={{ fontSize: 12, color: COLORS.outline }}>{t('gpx.pick.browse')}</div>
          <input
            ref={fileRef}
            type="file"
            accept=".gpx,application/gpx+xml"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }}
          />
        </div>
      </div>
      <PanelFooter>
        <div style={{ flex: 1 }} />
        <button className="wh-btn wh-btn-ghost" onClick={onClose}>{t('gpx.cancel')}</button>
      </PanelFooter>
    </>
  );
}

// ── Error stages ──────────────────────────────────────────────────────────

function ErrorStage({ kind, onClose, onRetry }: { kind: 'invalid' | 'empty' | 'toolarge'; onClose: () => void; onRetry: () => void }) {
  const { t } = useTranslation('map');
  const title = t(`gpx.error.${kind}Title`);
  const body = t(`gpx.error.${kind}Body`);
  return (
    <>
      <PanelHeader title={t('gpx.title')} onClose={onClose} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '32px 20px', textAlign: 'center' }}>
        <div
          style={{
            width: 56, height: 56, borderRadius: 999, background: COLORS.errorContainer,
            color: COLORS.error, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
          }}
        >
          <Icon name="warning" size={26} />
        </div>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 17, fontWeight: 500, color: COLORS.onSurface, marginBottom: 8 }}>
          {title}
        </div>
        <div style={{ fontSize: 13, color: COLORS.onSurfaceVariant, lineHeight: 1.55 }}>{body}</div>
      </div>
      <PanelFooter>
        <div style={{ flex: 1 }} />
        <button className="wh-btn wh-btn-ghost" onClick={onClose}>{t('gpx.cancel')}</button>
        <button className="wh-btn wh-btn-primary" onClick={onRetry}>{t('gpx.error.retry')}</button>
      </PanelFooter>
    </>
  );
}

// ── 2) Review stage ───────────────────────────────────────────────────────

function ReviewStage({
  filename, areaLayers, includedLayers, toggleLayer,
  trailCount, includeTrails, setIncludeTrails, includedTrailCount,
  categorySummary, includedCats, setIncludedCats, allCatsChecked, unknownPins,
  assign, setAssign, pinCategories, includedPinCount, includedAreaCount, onClose, onContinue,
}: {
  filename: string;
  areaLayers: { key: AreaLayerKey; icon: string; label: string; count: number }[];
  includedLayers: Set<AreaLayerKey>;
  toggleLayer: (key: AreaLayerKey) => void;
  trailCount: number;
  includeTrails: boolean;
  setIncludeTrails: (on: boolean) => void;
  includedTrailCount: number;
  categorySummary: { cat: GpxPinCategory; items: GpxImportPin[] }[];
  includedCats: Set<PinKind>;
  setIncludedCats: (s: Set<PinKind>) => void;
  allCatsChecked: boolean;
  unknownPins: GpxImportPin[];
  assign: Record<string, PinKind>;
  setAssign: (fn: (prev: Record<string, PinKind>) => Record<string, PinKind>) => void;
  pinCategories: GpxPinCategory[];
  includedPinCount: number;
  includedAreaCount: number;
  onClose: () => void;
  onContinue: () => void;
}) {
  const { t } = useTranslation('map');
  const catalog = usePinTypeCatalog();
  const toggleAll = () => {
    if (allCatsChecked) setIncludedCats(new Set());
    else setIncludedCats(new Set(categorySummary.map((c) => c.cat.type)));
  };
  const toggleCat = (type: PinKind) => {
    const next = new Set(includedCats);
    if (next.has(type)) next.delete(type); else next.add(type);
    setIncludedCats(next);
  };
  const canImport = includedAreaCount > 0 || includedTrailCount > 0 || includedPinCount > 0;

  const sectionLabel: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: COLORS.outline,
  };

  return (
    <>
      <PanelHeader title={t('gpx.title')} subtitle={filename} onClose={onClose} />
      <StepBar step={1} />
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '14px 16px 8px' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.onSurface, marginBottom: 2 }}>{t('gpx.review.title')}</div>
          <div style={{ fontSize: 12, color: COLORS.outline }}>{t('gpx.review.subtitle')}</div>
        </div>

        {unknownPins.length > 0 && (
          <div
            style={{
              margin: '0 12px 10px', padding: '9px 12px', background: COLORS.tertiaryContainer,
              color: COLORS.onTertiaryContainer, borderRadius: 8, display: 'flex', gap: 8,
              alignItems: 'flex-start', fontSize: 12,
            }}
          >
            <Icon name="warning" size={15} />
            <span>{t('gpx.review.unmappedWarn', { count: unknownPins.length })}</span>
          </div>
        )}

        {areaLayers.length > 0 && (
          <>
            <div style={{ ...sectionLabel, padding: '8px 16px 4px' }}>{t('gpx.section.areas')}</div>
            <div style={{ margin: '0 12px 4px', borderRadius: 10, border: `1px solid ${COLORS.outlineVariant}`, overflow: 'hidden' }}>
              {areaLayers.map((layer, idx) => {
                const on = includedLayers.has(layer.key);
                return (
                  <label
                    key={layer.key}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer',
                      background: COLORS.surfaceContainerLow, borderTop: idx > 0 ? `1px solid ${COLORS.surfaceContainerHighest}` : 'none',
                    }}
                  >
                    <input type="checkbox" className="wh-checkbox" checked={on} onChange={() => toggleLayer(layer.key)} />
                    <div style={{ width: 28, height: 28, borderRadius: 6, background: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name={layer.icon} size={14} color={COLORS.onPrimary} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.onSurface }}>{layer.label}</div>
                      <div style={{ fontSize: 11, color: COLORS.outline }}>{t('gpx.itemsCount', { count: layer.count })}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: COLORS.primaryOpacity12, color: COLORS.primary }}>
                      {layer.count}
                    </span>
                  </label>
                );
              })}
            </div>
          </>
        )}

        {trailCount > 0 && (
          <>
            <div style={{ ...sectionLabel, padding: '12px 16px 4px' }}>{t('gpx.section.trails')}</div>
            <div style={{ margin: '0 12px 4px', borderRadius: 10, border: `1px solid ${COLORS.outlineVariant}`, overflow: 'hidden' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', background: COLORS.surfaceContainerLow }}>
                <input type="checkbox" className="wh-checkbox" checked={includeTrails} onChange={() => setIncludeTrails(!includeTrails)} />
                <div style={{ width: 28, height: 28, borderRadius: 6, background: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name="edit" size={14} color={COLORS.onPrimary} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.onSurface }}>{t('gpx.section.trails')}</div>
                  <div style={{ fontSize: 11, color: COLORS.outline }}>{t('gpx.trailsCount', { count: trailCount })}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: COLORS.primaryOpacity12, color: COLORS.primary }}>
                  {trailCount}
                </span>
              </label>
            </div>
          </>
        )}

        {categorySummary.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px 4px' }}>
              <div style={sectionLabel}>{t('gpx.section.pins')}</div>
              <div style={{ flex: 1 }} />
              <button className="wh-btn wh-btn-text" style={{ fontSize: 12, padding: '2px 6px' }} onClick={toggleAll}>
                {allCatsChecked ? t('gpx.selectNone') : t('gpx.selectAll')}
              </button>
            </div>
            <div style={{ margin: '0 12px 4px', borderRadius: 10, border: `1px solid ${COLORS.outlineVariant}`, overflow: 'hidden' }}>
              {categorySummary.map(({ cat, items }, idx) => {
                const on = includedCats.has(cat.type);
                const isOther = cat.type === 'other';
                return (
                  <label
                    key={cat.type}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer',
                      background: COLORS.surfaceContainerLow, borderTop: idx > 0 ? `1px solid ${COLORS.surfaceContainerHighest}` : 'none',
                    }}
                  >
                    <input type="checkbox" className="wh-checkbox" checked={on} onChange={() => toggleCat(cat.type)} />
                    <img src={pinSrc(cat.type)} alt={catalog.label(cat.type)} width={26} height={26} style={{ flexShrink: 0, objectFit: 'contain' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.onSurface }}>{catalog.label(cat.type)}</div>
                      <div style={{ fontSize: 11, color: COLORS.outline }}>
                        {t('gpx.itemsCount', { count: items.length })}{isOther && ` · ${t('gpx.needsReview')}`}
                      </div>
                    </div>
                    {isOther ? (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: COLORS.tertiaryContainer, color: COLORS.onTertiaryContainer, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="warning" size={11} />{t('gpx.unmapped')}
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: COLORS.surfaceContainerHighest, color: COLORS.outline }}>
                        {items.length}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </>
        )}

        {unknownPins.length > 0 && (
          <>
            <div style={{ ...sectionLabel, padding: '12px 16px 4px' }}>{t('gpx.section.verify')}</div>
            <div style={{ margin: '0 12px', borderRadius: 10, border: `1px solid ${COLORS.outlineVariant}`, overflow: 'hidden' }}>
              {unknownPins.map((pin, idx) => (
                <PinRemapRow
                  key={pin.id}
                  pin={pin}
                  assignedType={assign[pin.id]}
                  pinCategories={pinCategories}
                  isFirst={idx === 0}
                  onAssign={(type) => {
                    setAssign((prev) => ({ ...prev, [pin.id]: type }));
                    setIncludedCats(new Set([...includedCats, type]));
                  }}
                />
              ))}
            </div>
          </>
        )}

        <div style={{ height: 12 }} />
      </div>
      <PanelFooter>
        <div style={{ flex: 1, fontSize: 12, color: COLORS.outline }}>
          {[
            includedAreaCount > 0 && t('gpx.areasCount', { count: includedAreaCount }),
            includedTrailCount > 0 && t('gpx.trailsCount', { count: includedTrailCount }),
            includedPinCount > 0 && t('gpx.pinsCount', { count: includedPinCount }),
          ].filter(Boolean).join(' · ')}
        </div>
        <button className="wh-btn wh-btn-ghost" onClick={onClose}>{t('gpx.cancel')}</button>
        <button className="wh-btn wh-btn-primary" onClick={onContinue} disabled={!canImport}>{t('gpx.confirmImport')}</button>
      </PanelFooter>
    </>
  );
}

// ── Pin remap row ─────────────────────────────────────────────────────────

function PinRemapRow({
  pin, assignedType, pinCategories, isFirst, onAssign,
}: {
  pin: GpxImportPin;
  assignedType: PinKind;
  pinCategories: GpxPinCategory[];
  isFirst: boolean;
  onAssign: (type: PinKind) => void;
}) {
  const catalog = usePinTypeCatalog();
  const isOther = assignedType === 'other';
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        background: COLORS.surfaceContainerLow, borderTop: isFirst ? 'none' : `1px solid ${COLORS.surfaceContainerHighest}`,
      }}
    >
      <img src={pinSrc(assignedType)} alt={assignedType} width={24} height={24} style={{ flexShrink: 0, objectFit: 'contain' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.onSurface, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {pin.name}
        </div>
        {pin.sym && <div style={{ fontSize: 11, color: COLORS.outline }}>{pin.sym}</div>}
      </div>
      <select
        value={assignedType}
        onChange={(e) => onAssign(e.target.value as PinKind)}
        style={{
          fontSize: 12, padding: '4px 6px', borderRadius: 6,
          border: `1.5px solid ${isOther ? COLORS.tertiary : COLORS.outlineVariant}`,
          background: isOther ? COLORS.tertiaryContainer : COLORS.surfaceContainerLowest,
          color: COLORS.onSurface, cursor: 'pointer', maxWidth: 110, flexShrink: 0,
        }}
      >
        {pinCategories.map((c) => (
          <option key={c.type} value={c.type}>{catalog.label(c.type)}</option>
        ))}
      </select>
    </div>
  );
}

// ── Done stage ────────────────────────────────────────────────────────────

type ImportSummary = { areas: number; trails: number; pins: number };

function DoneStage({ summary, onClose }: { summary: ImportSummary; onClose: () => void }) {
  const { t } = useTranslation('map');
  return (
    <>
      <PanelHeader title={t('gpx.title')} onClose={onClose} />
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 20px', textAlign: 'center' }}>
        <div
          style={{
            width: 52, height: 52, borderRadius: 999, background: COLORS.primary, color: COLORS.onPrimary,
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
          }}
        >
          <Icon name="check" size={26} />
        </div>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 19, fontWeight: 500, color: COLORS.onSurface, marginBottom: 6 }}>
          {t('gpx.done.title')}
        </div>
        <div style={{ fontSize: 13, color: COLORS.onSurfaceVariant, lineHeight: 1.5 }}>
          {(() => {
            const parts: string[] = [];
            if (summary.areas > 0) parts.push(t('gpx.areasCount', { count: summary.areas }));
            if (summary.trails > 0) parts.push(t('gpx.trailsCount', { count: summary.trails }));
            if (summary.pins > 0) parts.push(t('gpx.pinsCount', { count: summary.pins }));
            const added = parts.join(t('gpx.done.and'));
            return t('gpx.done.body', { added });
          })()}
        </div>
      </div>
      <PanelFooter>
        <div style={{ flex: 1 }} />
        <button className="wh-btn wh-btn-primary" onClick={onClose}>{t('gpx.done.close')}</button>
      </PanelFooter>
    </>
  );
}

// ── Existing-boundary confirmation dialog ─────────────────────────────────
// The import appends (never destroys existing geometry), so the choice is
// "add the GPX boundary alongside" vs "import only the pins".

function ReplaceBoundaryDialog({
  onAddAnyway, onWithoutBoundary, onCancel,
}: {
  onAddAnyway: () => void;
  onWithoutBoundary: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('map');
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, borderRadius: 12 }}>
      <div style={{ background: COLORS.surfaceContainerLowest, borderRadius: 12, padding: '20px 20px 16px', width: '100%' }}>
        <div style={{ fontFamily: COLORS.fontDisplay, fontSize: 16, fontWeight: 500, color: COLORS.onSurface, marginBottom: 8 }}>
          {t('gpx.replace.title')}
        </div>
        <div style={{ fontSize: 13, color: COLORS.onSurfaceVariant, lineHeight: 1.5, marginBottom: 18 }}>
          {t('gpx.replace.body')}
        </div>
        <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
          <button className="wh-btn wh-btn-primary" onClick={onAddAnyway} style={{ width: '100%' }}>{t('gpx.replace.addAnyway')}</button>
          <button className="wh-btn wh-btn-outline" onClick={onWithoutBoundary} style={{ width: '100%' }}>{t('gpx.replace.withoutBoundary')}</button>
          <button className="wh-btn wh-btn-ghost" onClick={onCancel} style={{ width: '100%' }}>{t('gpx.replace.cancel')}</button>
        </div>
      </div>
    </div>
  );
}
